import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import type { CalendarItem } from '@tempo/core';
import { IssuesPanel } from '../components/IssuesPanel';
import { ItemSheet } from '../components/ItemSheet';
import { NextUpCard } from '../components/NextUpCard';
import { DayTimeline } from '../components/DayTimeline';
import { FocusDay } from '../components/FocusDay';
import { Button, Loading, Segmented } from '../components/ui';
import { store } from '../lib/device';
import { dateOf, dayLong, dayName, duration, fromISODate, todayISO } from '../lib/format';
import { useNextUp, usePlan } from '../lib/hooks';
import { setParams, type Route } from '../lib/router';

export function TodayPage({ route, onAdd }: { route: Route; onAdd: (date: string) => void }) {
  const date = route.params.get('date') ?? todayISO();
  const isToday = date === todayISO();
  const plan = usePlan(date, 1);
  const next = useNextUp();
  const [selected, setSelected] = useState<CalendarItem | null>(null);
  // Focus = now / next / the rest folded into chunks. Full day = every item, full detail.
  const [view, setView] = useState<'focus' | 'list'>(() => store.get('tempo.todayView', 'focus'));
  const changeView = (v: 'focus' | 'list') => {
    setView(v);
    store.set('tempo.todayView', v);
  };
  const [showNotes, setShowNotes] = useState(false);
  const headsUps = (plan.data?.issues ?? []).filter((i) => i.date === date && i.severity !== 'info');

  const go = (delta: number) => {
    const d = fromISODate(date).plus({ days: delta }).toISODate()!;
    setParams({ date: d === todayISO() ? null : d });
  };

  const items = plan.data?.items ?? [];
  const dayItems = items.filter((i) => dateOf(i.start) === date || i.kind === 'sleep');
  const sum = (kind: string) => dayItems.filter((i) => i.kind === kind).reduce((m, i) => m + (i.end - i.start), 0);
  const studyMs = sum('task');
  const travelMs = sum('travel');
  const fixedCount = dayItems.filter((i) => i.kind === 'event' && i.busy).length;
  const routineCount = dayItems.filter((i) => i.kind === 'routine').length;

  return (
    <div className="page">
      <div className="page-head">
        <div className="grow">
          <h1>{dayLong(date)}</h1>
          <div className="subtle small">{isToday ? 'Today' : dayName(fromISODate(date).toMillis())}</div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <Button icon size="sm" onClick={() => go(-1)} aria-label="Previous day">
            <ChevronLeft size={18} />
          </Button>
          {!isToday && (
            <Button size="sm" onClick={() => setParams({ date: null })}>
              Today
            </Button>
          )}
          <Button icon size="sm" onClick={() => go(1)} aria-label="Next day">
            <ChevronRight size={18} />
          </Button>
          <Button variant="primary" size="sm" className="desktop-only-btn" onClick={() => onAdd(date)}>
            <Plus size={16} /> Add
          </Button>
        </div>
      </div>
      <div className="view-toggle">
        <Segmented
          value={view}
          onChange={changeView}
          options={[
            { value: 'focus', label: 'Focus' },
            { value: 'list', label: 'Full day' },
          ]}
        />
      </div>

      {plan.isLoading ? (
        <Loading />
      ) : plan.error ? (
        <div className="callout danger">{(plan.error as Error).message}</div>
      ) : view === 'focus' ? (
        <div className="focus-wrap">
          <FocusDay date={date} items={items} isToday={isToday} next={next.data} onSelect={setSelected} />
          {headsUps.length > 0 && (
            <div className="notes-toggle">
              <button className="btn ghost sm" onClick={() => setShowNotes(!showNotes)}>
                {showNotes ? 'Hide' : 'Show'} {headsUps.length} heads-up{headsUps.length === 1 ? '' : 's'} for today
              </button>
              {showNotes && <IssuesPanel issues={headsUps} />}
            </div>
          )}
        </div>
      ) : (
        <div className="today-grid">
          <div className="stack">
            {isToday && next.data && (
              <div className="mobile-only">
                <NextUpCard next={next.data} />
              </div>
            )}
            <div className="mobile-only">
              <IssuesPanel issues={plan.data!.issues} date={date} compact />
            </div>
            <div className="card" style={{ padding: 8 }}>
              <DayTimeline date={date} items={dayItems} onSelect={setSelected} />
            </div>
          </div>
          <div className="stack desktop-only">
            {isToday && next.data && <NextUpCard next={next.data} />}
            <div className="card">
              <div className="grid-2" style={{ gap: 14 }}>
                <Stat label="Fixed" value={String(fixedCount)} />
                <Stat label="Routines" value={String(routineCount)} />
                <Stat label="Study" value={studyMs ? duration(studyMs) : '–'} />
                <Stat label="Travel" value={travelMs ? duration(travelMs) : '–'} />
              </div>
            </div>
            <IssuesPanel issues={plan.data!.issues} date={date} compact />
          </div>
        </div>
      )}
      {selected && <ItemSheet item={selected} items={items} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="tiny faint" style={{ fontWeight: 650, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}
